/**
 * TafsirPopup — long-press an ayah → certified tafsir in a SlideSheet.
 *
 * Data source: https://api.quran.com/api/v4/tafsirs/{id}/by_ayah/{verseKey}
 *   - EN: tafsir 169 = Tafsir Ibn Kathir (abridged) — Darussalam
 *   - AR: tafsir 16  = Tafsir al-Muyassar — King Fahd Complex
 *
 * Attribution: MANDATORY per CLAUDE.md — the tafsir source name + publisher
 * must appear in the SlideSheet footer.
 *
 * Usage:
 *   import { showTafsirPopup } from './TafsirPopup';
 *   await showTafsirPopup('2:255', ctx);
 */

import type { AweniContext } from '../../types';
import { SlideSheet } from './components/SlideSheet';
import { getAsbabEntry } from './asbab-nuzul';
import { sanitizeQuranHtml } from './sanitize-html';

// ─── Tafsir source config ──────────────────────────────────────────────────

interface TafsirSource {
  id: number;
  name: string;
  publisher: string;
}

const TAFSIR_SOURCES: Record<string, TafsirSource> = {
  // English — Tafsir Ibn Kathir abridged, published by Darussalam
  en: { id: 169, name: 'Tafsir Ibn Kathir (abridged)', publisher: 'Darussalam' },
  // French — Tafsir al-Muyassar (French edition)
  fr: { id: 187, name: 'Tafsīr al-Muyassar', publisher: 'King Fahd Complex' },
  // Arabic — Tafsir al-Muyassar, King Fahd Complex
  ar: { id: 16, name: 'تفسير الميسر', publisher: 'مجمع الملك فهد' },
  // Spanish — fall back to English source
  es: { id: 169, name: 'Tafsir Ibn Kathir (abridged)', publisher: 'Darussalam' },
  // German — fall back to English source
  de: { id: 169, name: 'Tafsir Ibn Kathir (abridged)', publisher: 'Darussalam' },
};

const DEFAULT_SOURCE = TAFSIR_SOURCES['en'];

// ─── API shape ─────────────────────────────────────────────────────────────

interface TafsirApiResponse {
  tafsir?: {
    text?: string;
    resource_name?: string;
  };
}

// ─── Main exported function ────────────────────────────────────────────────

/**
 * Fetch the tafsir for `verseKey` and display it in a SlideSheet.
 * Resolves when the sheet is open (not when it's closed).
 */
export async function showTafsirPopup(
  verseKey: string,
  ctx: AweniContext,
): Promise<void> {
  const locale = ctx.i18n.locale ?? 'en';
  const source: TafsirSource = TAFSIR_SOURCES[locale] ?? DEFAULT_SOURCE;
  const t = (key: string, fallback: string) => ctx.i18n.t(key) || fallback;

  // Haptic feedback on open
  navigator.vibrate?.(10);

  const sheet = new SlideSheet({
    title: `${t('hisni_tafsir_title', 'Tafsir')} — ${verseKey}`,
    closeOnOverlay: true,
  });

  // Show loading state immediately
  sheet.setContent(_loadingHtml(t));
  sheet.open();

  try {
    const url = `https://api.quran.com/api/v4/tafsirs/${source.id}/by_ayah/${encodeURIComponent(verseKey)}`;
    const res = await fetch(url, { cache: 'force-cache' });

    if (!res.ok) throw new Error(`Tafsir API HTTP ${res.status}`);

    const json = (await res.json()) as TafsirApiResponse;
    const rawText = json?.tafsir?.text ?? '';
    const safeHtml = sanitizeQuranHtml(rawText);

    const asbab = getAsbabEntry(verseKey);

    sheet.setContent(_renderContent(verseKey, safeHtml, asbab, source, t));
  } catch {
    sheet.setContent(_errorHtml(t));
  }
}

// ─── HTML builders ─────────────────────────────────────────────────────────

function _loadingHtml(t: (k: string, fb: string) => string): string {
  return `
    <div class="htp-loading" role="status" aria-live="polite">
      <div class="htp-spinner" aria-hidden="true"></div>
      <p>${escHtml(t('hisni_tafsir_loading', 'Loading tafsir…'))}</p>
    </div>
    ${_styleBlock()}
  `;
}

function _errorHtml(t: (k: string, fb: string) => string): string {
  return `
    <div class="htp-error" role="alert">
      <p>${escHtml(t('hisni_tafsir_error', 'Could not load tafsir. Please try again.'))}</p>
    </div>
    ${_styleBlock()}
  `;
}

function _renderContent(
  verseKey: string,
  safeHtml: string,
  asbab: ReturnType<typeof getAsbabEntry>,
  source: TafsirSource,
  t: (k: string, fb: string) => string,
): string {
  const asbabBlock = asbab
    ? `<section class="htp-asbab" aria-label="${escHtml(t('hisni_tafsir_asbab_label', 'Occasion of Revelation'))}">
        <h3 class="htp-section-title">${escHtml(t('hisni_tafsir_asbab_title', 'Occasion of Revelation (Asbāb al-Nuzūl)'))}</h3>
        <p class="htp-asbab-text">${escHtml(asbab.text)}</p>
        <p class="htp-asbab-source">${escHtml(asbab.source)}</p>
      </section>`
    : '';

  const attribution = `${source.name} — ${source.publisher}`;

  return `
    <article class="htp-article" lang="${_langForSource(source)}" aria-label="${escHtml(t('hisni_tafsir_article_label', 'Tafsir text'))} ${escHtml(verseKey)}">
      <div class="htp-tafsir-text">${safeHtml || escHtml(t('hisni_tafsir_empty', 'No tafsir text available for this verse.'))}</div>
      ${asbabBlock}
    </article>
    <footer class="htp-footer" role="contentinfo">
      <p class="htp-attribution" aria-label="${escHtml(t('hisni_tafsir_attribution_label', 'Source'))}">
        📖 ${escHtml(attribution)}
      </p>
    </footer>
    ${_styleBlock()}
  `;
}

function _langForSource(source: TafsirSource): string {
  // Arabic publishers → render in ar; everything else in the locale
  if (source.publisher.includes('مجمع') || source.id === 16) return 'ar';
  return 'en';
}

let _stylesInjected = false;
function _styleBlock(): string {
  if (_stylesInjected) return '';
  _stylesInjected = true;
  return `<style id="htp-styles">
    .htp-loading, .htp-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 32px 16px;
      color: var(--ln-text-secondary);
      font-size: .9rem;
      text-align: center;
    }
    .htp-spinner {
      width: 28px;
      height: 28px;
      border: 3px solid var(--ln-border, rgba(0,0,0,.12));
      border-top-color: var(--ln-accent, #0A0A0A);
      border-radius: 50%;
      animation: htp-spin var(--motion-duration-deliberate) linear infinite;
    }
    @keyframes htp-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      .htp-spinner { animation: none; border-top-color: var(--ln-accent); }
    }

    .htp-article {
      padding: 0 0 16px;
    }
    .htp-tafsir-text {
      font-size: .95rem;
      line-height: 1.75;
      color: var(--ln-text-primary, #111);
      font-family: var(--ln-font-ui, system-ui, sans-serif);
    }
    .htp-tafsir-text p { margin: 0 0 .9em; }

    .htp-asbab {
      margin-top: 20px;
      padding: 12px 14px;
      background: var(--ln-bg-subtle, rgba(0,0,0,.04));
      border-radius: var(--ln-radius-md, 10px);
      border-inline-start: 3px solid var(--ln-accent, #0A0A0A);
    }
    .htp-section-title {
      font-size: .78rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .06em;
      color: var(--ln-accent, #0A0A0A);
      margin: 0 0 8px;
    }
    .htp-asbab-text {
      font-size: .88rem;
      line-height: 1.6;
      color: var(--ln-text-primary, #111);
      margin: 0 0 6px;
    }
    .htp-asbab-source {
      font-size: .75rem;
      color: var(--ln-text-secondary);
      margin: 0;
      font-style: italic;
    }

    .htp-footer {
      margin-top: 20px;
      padding-top: 12px;
      border-top: 1px solid var(--ln-border, rgba(0,0,0,.08));
    }
    .htp-attribution {
      font-size: .75rem;
      color: var(--ln-text-secondary);
      margin: 0;
    }

    .dark .htp-asbab {
      background: rgba(255,255,255,.06);
    }
  </style>`;
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
